import { useState } from 'react';
import apiClient from '../clients/apiClient';
import { showGlobalToast } from '../utils/toast';
import { useAuth } from './useAuth';

export type BuyablePack = 'starter' | 'growth' | 'pro';

interface CreateOrderResponse {
  order_id: string;
  key_id: string;
  amount_cents: number;
  currency: string;
  credits: number;
}

interface RazorpayPaymentResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  notes?: Record<string, string>;
  handler: (response: RazorpayPaymentResponse) => void;
  prefill?: { name?: string; email?: string };
  theme?: { color?: string };
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open(): void;
  on(event: string, handler: (response: unknown) => void): void;
}

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

export function useBuyCredits() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState<BuyablePack | null>(null);

  async function buyCredits(pack: BuyablePack) {
    if (loading) return;
    setLoading(pack);

    let order: CreateOrderResponse;
    try {
      const { data } = await apiClient.post<CreateOrderResponse>('/api/payments/create-order', { pack });
      order = data;
    } catch {
      setLoading(null);
      showGlobalToast('Could not initiate payment. Please try again.', 'error');
      return;
    }

    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount_cents,
      currency: order.currency,
      name: 'EextraMile AI',
      description: `${pack.charAt(0).toUpperCase() + pack.slice(1)} Pack — ${order.credits} credits`,
      order_id: order.order_id,
      notes: {
        user_id: user?.id ?? '',
        pack,
      },
      handler: async (response: RazorpayPaymentResponse) => {
        try {
          await apiClient.post('/api/payments/verify', {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          await refreshUser();
          showGlobalToast(`${order.credits} credits added to your account!`, 'success');
        } catch {
          showGlobalToast(
            'Payment received. Credits will appear shortly — contact support if they don\'t.',
            'info',
          );
        } finally {
          setLoading(null);
        }
      },
      prefill: {
        name: user?.full_name ?? '',
        email: user?.email ?? '',
      },
      theme: { color: '#06b6d4' },
      modal: {
        ondismiss: () => setLoading(null),
      },
    });

    rzp.on('payment.failed', () => {
      setLoading(null);
      showGlobalToast('Payment failed. Please try again.', 'error');
    });

    rzp.open();
  }

  return { buyCredits, loading };
}
